import { notesApi, selectOrganizationId } from '@carboncopyco/data';
import type { NoteEntityType, Play, PlayAsset } from '@carboncopyco/types';
import type { ModalProps } from '@carboncopyco/ui';
import { usePlayAnalytics } from '@carboncopyco/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/hooks';
import { CustomizeModal } from './CustomizeModal';
import { GiveADemoContextProvider } from './GiveADemoContext';
import { OpportunityModal } from './OpportunityModal';
import { playParentEntityToIdKey } from './consts';

type GiveADemoFlowModalWrapperProps = {
  open: boolean;
  asset: PlayAsset;
  filesServiceUrl: string;
  workspaceId: string;
  onClose?: (redirectUrl?: string) => void;
  onPresent: (presentation: Play) => void;
  onSave?: (presentation: Play) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  withNotificationOnPresent?: boolean;
  hidePlayLater?: boolean;
  hideCloseButton?: ModalProps['removeCloseIcon'];
};

export const GiveADemoFlowModal = ({
  open,
  asset,
  filesServiceUrl,
  onClose = () => {},
  onPresent,
  onSave = () => {},
  onLoadingChange,
  withNotificationOnPresent = false,
  hidePlayLater,
  workspaceId,
  hideCloseButton,
}: GiveADemoFlowModalWrapperProps) => {
  const { trackPlaySaveCustomizations, trackPlayDiscardCustomizations } =
    usePlayAnalytics('give_a_demo_modal_customize_modal', {
      [playParentEntityToIdKey[asset.type]]: asset.id,
    });
  const [customizeModalOpen, setCustomizeModalOpen] = useState(false);
  const organizationId = useSelector(selectOrganizationId);
  const dispatch = useAppDispatch();
  const flags = useFlags();
  const noteFetches = flags.speakerNotes
    ? asset.assets
        .filter((asset) => asset.assetType !== 'externalUrl')
        .map(async (asset) => {
          return dispatch(
            notesApi.endpoints.getNotes.initiate({
              organizationId,
              entityType: asset.assetType as NoteEntityType,
              entityId: String(asset.entity.id),
            })
          );
        })
    : [];
  const speakerNotes = {};
  Promise.all(noteFetches).then((response) => {
    response.forEach((resolved) => {
      speakerNotes[resolved.originalArgs.entityId] =
        resolved.data?.[0]?.content;
    });
  });

  const { type, id } = asset;
  return (
    <GiveADemoContextProvider
      filesServiceUrl={filesServiceUrl}
      speakerNotes={speakerNotes}
      playSource={{ id, type }}
    >
      <OpportunityModal
        workspaceId={workspaceId}
        open={open}
        asset={asset}
        onClose={onClose}
        onPresent={onPresent}
        onSave={onSave}
        onCustomize={() => setCustomizeModalOpen(true)}
        onLoadingChange={onLoadingChange}
        containerClass={customizeModalOpen ? 'hidden' : '-mt-5'}
        withNotificationOnPresent={withNotificationOnPresent}
        hidePlayLater={hidePlayLater}
        hideCloseButton={hideCloseButton}
      />
      {customizeModalOpen && (
        <CustomizeModal
          open={open}
          assets={asset.assets}
          onClose={() => {
            trackPlayDiscardCustomizations();
            setCustomizeModalOpen(false);
          }}
          onSubmit={() => {
            trackPlaySaveCustomizations();
            setCustomizeModalOpen(false);
          }}
        />
      )}
    </GiveADemoContextProvider>
  );
};
